import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { makeGetCellValue, selectSeqNum, selectUserId } from '../../../redux/globalsSlice';
import { 
   EVENT_TYPE_AMEND_ORDER, DURATION_IMMEDIATE, DURATION_DAY, DURATION_GTC,
   GetMinStep, SELL_SIDE, Get10Power, CODE_HINT, ACCOUNT_HINT, PRICE_DECIMALS, QTY_DECIMALS, ShowError, confirmationDialog,
   JSON_KEY_INSTRUMENT, JSON_KEY_DURATION, 
   JSON_KEY_USER, 
   EVENT_TOPIC_FOR_TRANSACTION_SERVER, JSON_KEY_MESSAGE_TYPE,
   DROPDOWN_LIST_NONE,
   DROPDOWN_LIST_NONE_ID,
   JSON_KEY_SELL_TRADING_ACCOUNT,
   JSON_KEY_SELL_QUANTITY,
   JSON_KEY_SELL_PRICE,
   JSON_KEY_BUY_TRADING_ACCOUNT,
   JSON_KEY_BUY_QUANTITY,
   JSON_KEY_BUY_PRICE,
   YES,
   JSON_KEY_REPLACE,
   JSON_KEY_BUY_ORDER_NUMBER,
   JSON_KEY_SELL_ORDER_NUMBER,
} from '../../../common/common';
import { DispatchTransactionEvent } from '../../events/events';
import { useForm } from 'react-hook-form';
import { MSG_TYPE_ORDER_PAIR, MSG_TYPE_ORDER_PAIR_REPLACE } from '../../../common/msg_types';

export function OrderPairLogic(onClose) {
   const { register, handleSubmit, } = useForm();

   const [replace, setReplace] = useState(YES);
   const [buyOrderNumber, setBuyOrderNumber] = useState('');
   const [sellOrderNumber, setSellOrderNumber] = useState('');
   const [code, setCode] = useState('');
   const [buyTradingAccount, setBuyTradingAccount] = useState('');
   const [sellTradingAccount, setSellTradingAccount] = useState('');
   const [verb, setVerb] = useState('');
   const [buyPrice, setBuyPrice] = useState('');
   const [buyQty, setBuyQty] = useState('');
   const [sellPrice, setSellPrice] = useState('');
   const [sellQty, setSellQty] = useState('');
   const [visible_qty, setVisibleQty] = useState('');
   const [visible_balance, setVisibleBalance] = useState('');
   const [total_balance, setTotalBalance] = useState('');
   const [durationType, setDurationType] = useState(DURATION_DAY);
   const [specialType, setSpecialType] = useState(DROPDOWN_LIST_NONE_ID);
   const [session, setSession] = useState(DROPDOWN_LIST_NONE);
   const [buySell, setBuySell] = useState(SELL_SIDE);
   const [qty_step, setQtyStep] = useState(1);
   const [price_step, setPriceStep] = useState(0.01);
   const getDecimalValue = useMemo(() => makeGetCellValue(), []);
   const price_dec = useSelector((state) => getDecimalValue(state, code, PRICE_DECIMALS));
   const qty_dec = useSelector((state) => getDecimalValue(state, code, QTY_DECIMALS));
   const [triggerCondition, setTriggerCondition] = useState(DROPDOWN_LIST_NONE);
   const [triggerPrice, setTriggerPrice] = useState(0);
   const userId = useSelector(selectUserId);

   useEffect(() => {
      if (price_dec !== undefined) {
        setPriceStep(GetMinStep(price_dec));
      }
    }, [price_dec]);
    
   useEffect(() => {
      if (qty_dec !== undefined) {
         setQtyStep(GetMinStep(qty_dec));
      }
   }, [qty_dec]);

   const inSeqNum = useSelector(selectSeqNum);
   const inSeqNumRef = useRef(inSeqNum);

   useEffect(() => {       // to get the latest seqnum
      inSeqNumRef.current = inSeqNum;
   }, [inSeqNum]);

   const isValid = Number(buyQty) > 0 && Number(buyPrice) > 0;

   const onSubmit = async () => {
      if ((code === CODE_HINT) || (code === '')) {
         ShowError("Instrument Code is missing");
         return;      
      }

      if ((buyTradingAccount === ACCOUNT_HINT) || (buyTradingAccount === '')) {
         ShowError("Buy Account is missing");
         return;      
      }
      
      if (buyPrice === '') {
         ShowError("Buy price is missing");
         return;
      }

      if (buyQty === '') {
         ShowError("Buy quantity is missing");
         return;
      }

      if ((sellTradingAccount === ACCOUNT_HINT) || (sellTradingAccount === '')) {
         ShowError("Sell Account is missing");
         return;      
      }

      if (sellPrice === '') {
         ShowError("Sell price is missing");
         return;
      }

      if (sellQty === '') {
         ShowError("Sell quantity is missing");
         return;
      }

      const buyPriceNum = parseFloat(buyPrice);
      const sellPriceNum = parseFloat(sellPrice);
      
      if (isNaN(buyPriceNum) || isNaN(sellPriceNum)) {
         ShowError("Please enter valid numbers for buy and sell prices");
         return false;
      }
      
      if (buyPriceNum >= sellPriceNum) {
         ShowError(`Buy price (${buyPrice}) must be less than sell price (${sellPrice})`);
         return false;
      }
      
      // if (buyPrice >= sellPrice) {

      //    ShowError("Buy price must be less than sell price" + buyPrice + ", " + sellPrice);
      //    return;
      // }

      const buyRounderPrice = Math.round(buyPrice * Get10Power(price_dec));
      const buyRoundQuantity = Math.round(buyQty * Get10Power(qty_dec));
      const sellRounderPrice = Math.round(sellPrice * Get10Power(price_dec));
      const sellRoundQuantity = Math.round(sellQty * Get10Power(qty_dec));
      
      const order_pair = {
         [JSON_KEY_MESSAGE_TYPE]: MSG_TYPE_ORDER_PAIR,
         [JSON_KEY_USER]: userId,     
         [JSON_KEY_REPLACE]: replace,
         [JSON_KEY_INSTRUMENT]: code,
         [JSON_KEY_BUY_QUANTITY]: buyRoundQuantity,
         [JSON_KEY_SELL_QUANTITY]: sellRoundQuantity,
         [JSON_KEY_BUY_TRADING_ACCOUNT]: buyTradingAccount,
         [JSON_KEY_SELL_TRADING_ACCOUNT]: sellTradingAccount,
         [JSON_KEY_DURATION]: durationType,    
         [JSON_KEY_BUY_PRICE]: buyRounderPrice,
         [JSON_KEY_SELL_PRICE]: sellRounderPrice,
      };

      if (replace === YES) {
         order_pair[JSON_KEY_MESSAGE_TYPE] = MSG_TYPE_ORDER_PAIR_REPLACE;
         order_pair[JSON_KEY_BUY_ORDER_NUMBER] = buyOrderNumber;
         order_pair[JSON_KEY_SELL_ORDER_NUMBER] = sellOrderNumber;
      }

      // build confirmation message
      let message = '';
      
      message += 'Buy Qty/Price: ' + buyQty + "@" + buyPrice + ", Sell Qty/Price: " + sellQty + "@" + sellPrice;

      switch (durationType) {
         case DURATION_IMMEDIATE:
            message += ' and duration Immediate.';
            break;
         case DURATION_DAY:
            message += ' and duration Day.';
            break;
         case DURATION_GTC:
            message += ' and duration GTC.';
            break;   
         default:
            message += ' and duration Unknown.';
            break;
      }
      
      if (replace === YES) {
         message += ' To replace buy order number: ' + buyOrderNumber + ' and sell order number: ' + sellOrderNumber;
      }

      // confirmation check
      const confirmed = await confirmationDialog({
         title: 'Order Pair for ' + code,
         text: message,
      });

      if (confirmed) {
         // console.log('Submitting order amend:', order_pair);
         DispatchTransactionEvent(EVENT_TOPIC_FOR_TRANSACTION_SERVER, EVENT_TYPE_AMEND_ORDER, order_pair); 
         onClose();
      }
   };

   return {
      buyOrderNumber, setBuyOrderNumber, 
      sellOrderNumber, setSellOrderNumber, 
      buyTradingAccount, setBuyTradingAccount, sellTradingAccount, setSellTradingAccount, verb, setVerb,
      durationType, code,
      setDurationType, setCode,
      buyPrice, setBuyPrice, 
      sellPrice, setSellPrice,
      price_dec, 
      buyQty, setBuyQty,
      sellQty, setSellQty, 
      qty_dec,
      visible_qty, setVisibleQty,
      visible_balance, setVisibleBalance, 
      triggerCondition, setTriggerCondition,
      triggerPrice, setTriggerPrice,
      specialType, setSpecialType,
      session, setSession,
      total_balance, setTotalBalance, 
      buySell, setBuySell,
      qty_step, setQtyStep, price_step, setPriceStep,
      isValid, 
      handleSubmit, onSubmit, register,
      replace, setReplace,
   };
}

